import { Box, Chip } from '@mui/material'
import { darken, lighten, useTheme } from '@mui/material/styles'
import type { ChatReactionAggregate } from '@/api/chat'

/** Шаг «тона»: подложка реакции — тон(а) темнее фона пузыря, hover — тон светлее. */
const TONE_STEP = 0.1

interface MessageReactionsProps {
  reactions: ChatReactionAggregate[]
  currentUserId: number
  isOwn: boolean
  /** Сообщение без пузыря (стикер): тона считаются от фона ленты, не от primary. */
  onBareBackground?: boolean
  onToggle: (emoji: string) => void
}

/**
 * Ряд реакций под содержимым пузыря: клик по чипу — toggle своей реакции.
 * Новая реакция ставится через контекстное меню сообщения (правый клик).
 * Доступно любому участнику комнаты — по can_write реакции не дизейблятся.
 */
export function MessageReactions({
  reactions,
  currentUserId,
  isOwn,
  onBareBackground = false,
  onToggle,
}: MessageReactionsProps) {
  const theme = useTheme()

  if (reactions.length === 0) return null

  // Фон-основа для тонов: свои пузыри — primary.main, чужие и «голые»
  // стикеры — серый (визуальный эквивалент action.hover поверх paper).
  const bubbleColor =
    isOwn && !onBareBackground
      ? theme.palette.primary.main
      : theme.palette.mode === 'light'
        ? theme.palette.grey[100]
        : theme.palette.grey[800]

  return (
    <Box
      sx={{
        display: 'flex',
        alignItems: 'center',
        // По стороне сообщения: свои — справа, собеседника — слева
        justifyContent: isOwn ? 'flex-end' : 'flex-start',
        flexWrap: 'wrap',
        gap: 0.5,
        mt: 0.5,
      }}
    >
      {reactions.map((reaction) => {
        const isMine = reaction.user_ids.includes(currentUserId)
        const resting = darken(bubbleColor, TONE_STEP * (isMine ? 2 : 1))
        const hover = lighten(resting, TONE_STEP)

        return (
          <Chip
            key={reaction.emoji}
            label={`${reaction.emoji} ${reaction.count}`}
            size="small"
            clickable
            onClick={() => onToggle(reaction.emoji)}
            sx={{
              height: 20,
              fontSize: '0.75rem',
              borderRadius: '10px',
              border: 0,
              bgcolor: resting,
              color: 'inherit',
              '& .MuiChip-label': { px: 0.75 },
              '&:hover': { bgcolor: hover },
            }}
          />
        )
      })}
    </Box>
  )
}
